import {
  memo,
  useCallback,
  useMemo,
  useState,
  Fragment,
  useContext,
} from "react";

import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import { ButtonV1, ModalBox } from "components";
import FGUpdate from "./FGUpdate";
import FGBulkActions from "./FGBulkActions";
import FGConfirmDialog from "./FGConfirmDialog";
import FGRefreshContext from "../FGRefreshContext";
import FGHistory from "./FGHistory";

import { postCall_v2, putCall_v2 } from "services";

import { useToaster } from "hooks";
import { formatDialogDate } from "utils";

import { error, success } from "constants";
import {
  FG_BULK_ACTION_MODE,
  FG_LINE_ITEM_ENDPOINTS,
  FG_TRACKING_LABELS,
  LINE_ITEM_COLUMNS,
} from "../constants";

import {
  getBulkConfirmationMessage,
  getExpectedFgTone,
  getFgAwaitedTone,
  getFgDueByTone,
  getFgToneColor,
  getIsUpdateFgHidden,
  getLineItemFgReadyTone,
  getQuantityBreakdownTooltip,
  parseMtValue,
} from "../makeData";

const BatchUpdatesList = ({ batchUpdates, theme }) => {
  if (!batchUpdates?.length) return null;

  const batchText = batchUpdates
    .map((batch) => `${batch.batch} × ${batch.qty}`)
    .join(", ");

  return (
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: 13,
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: 200,
      }}
    >
      {batchText}
    </Typography>
  );
};

const FGLineItemTable = ({ order, selectedLineItemIds, onToggleSelectAll }) => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const refreshDataInBackground = useContext(FGRefreshContext);
  const [bulkActionMode, setBulkActionMode] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [openFgUpdates, setOpenFgUpdates] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [fgUpdateData, setFgUpdateData] = useState({});
  const [historyOpenForLineItem, setHistoryOpenForLineItem] = useState(null);

  const selectedLineItemIdSet = useMemo(
    () => new Set(selectedLineItemIds),
    [selectedLineItemIds],
  );

  const openFgUpdateIds = useMemo(
    () => new Set(openFgUpdates),
    [openFgUpdates],
  );

  const eligibleItems = useMemo(
    () => order.lineItems.filter((item) => item.eligible),
    [order.lineItems],
  );

  const eligibleIds = useMemo(
    () => eligibleItems.map((item) => item.id),
    [eligibleItems],
  );

  const selectedEligibleCount = useMemo(
    () => eligibleIds.filter((id) => selectedLineItemIdSet.has(id)).length,
    [eligibleIds, selectedLineItemIdSet],
  );

  const effectiveSelectedEligibleCount =
    selectedEligibleCount || eligibleIds.length;

  const allEligibleSelected =
    eligibleIds.length > 0 && selectedEligibleCount === eligibleIds.length;

  const isEligiblePartiallySelected =
    selectedEligibleCount > 0 && !allEligibleSelected;

  const totalEligibleQty = useMemo(() => {
    return order.lineItems.reduce((sum, item) => {
      return sum + parseMtValue(item.notInvoicedQty);
    }, 0);
  }, [order.lineItems]);

  const totalLineItemsQty = useMemo(
    () =>
      order.lineItems.reduce((sum, item) => {
        return sum + parseMtValue(item.orderQty);
      }, 0),
    [order.lineItems],
  );

  const selectedEligibleReducedCount = useMemo(
    () =>
      order.lineItems.filter(
        (item) =>
          selectedLineItemIdSet.has(item.id) && parseMtValue(item.fgReady) > 0,
      ).length,
    [selectedLineItemIdSet, order.lineItems],
  );

  const showExpectedFgWarning =
    bulkActionMode === FG_BULK_ACTION_MODE.EXPECTED_FG;
  const isFgReadySelected = bulkActionMode === FG_BULK_ACTION_MODE.FG_READY;
  const isExpectedFgSelected =
    bulkActionMode === FG_BULK_ACTION_MODE.EXPECTED_FG;
  const shouldShowActionMessage = Boolean(bulkActionMode);
  const isDueDateMissing = isExpectedFgSelected && !dueDate;

  const closeFgUpdate = useCallback((lineItemId) => {
    setOpenFgUpdates((prev) => prev.filter((id) => id !== lineItemId));
    setFgUpdateData((prev) => {
      const updated = { ...prev };
      delete updated[lineItemId];
      return updated;
    });
  }, []);

  const handleToggleFgUpdate = useCallback(
    async (lineItem) => {
      const isOpen = openFgUpdateIds.has(lineItem.lineItemId);

      if (isOpen) {
        closeFgUpdate(lineItem.lineItemId);
        return;
      }

      try {
        const res = await postCall_v2(FG_LINE_ITEM_ENDPOINTS.fetchUpdates, {
          orderId: order.orderId,
          sellerOrderId: lineItem.sellerOrderId,
          lineitemId: lineItem.lineItemId,
        });
        setFgUpdateData((prev) => ({
          ...prev,
          [lineItem.lineItemId]: res.data.data,
        }));
        setOpenFgUpdates((prev) => [...prev, lineItem.lineItemId]);
      } catch {
        triggerToaster("Failed to fetch FG details", error);
      }
    },
    [closeFgUpdate, openFgUpdateIds, order.orderId, triggerToaster],
  );

  const updateFgReady = useCallback(
    async (payload) => {
      try {
        await putCall_v2(FG_LINE_ITEM_ENDPOINTS.update, payload);
        triggerToaster("FG updated successfully", success);
        if (payload?.actionType === "LINE_ITEM_UPDATE") {
          closeFgUpdate(payload?.updates[0]?.lineItemId);
        }
        refreshDataInBackground();
        return true;
      } catch (err) {
        triggerToaster(err?.data?.errorMessage || "Failed to update FG", error);
        return false;
      }
    },
    [closeFgUpdate, refreshDataInBackground, triggerToaster],
  );

  const handleFGUpdateData = useCallback(
    async (data, lineItem) => {
      const payload = {
        orderId: order.orderId,
        actionType: "LINE_ITEM_UPDATE",
        updates: [
          {
            lineItemId: lineItem.lineItemId,
            reason: data.reason || null,
            fgReady: {
              quantity: Number(data.fgReadyNow),
            },
            expectedFgData: data.fgBatches
              .filter(
                (batch) =>
                  !batch.isReadOnly &&
                  (batch.id ||
                    batch.qty ||
                    batch.readinessDate ||
                    batch.sellerOrderRef ||
                    batch.sellerOLRef),
              )
              .map((batch) => ({
                id: batch.id || null,
                quantity: Number(batch.qty),
                readinessDate: batch.readinessDate
                  ? new Date(batch.readinessDate).getTime()
                  : null,
                sellerOrderRef: batch.sellerOrderRef || null,
                sellerOlRef: batch.sellerOLRef || null,
              })),
          },
        ],
      };

      await updateFgReady(payload);
    },
    [order.orderId, updateFgReady],
  );

  const confirmationItems = useMemo(
    () => order.lineItems.filter((item) => selectedLineItemIdSet.has(item.id)),
    [order.lineItems, selectedLineItemIdSet],
  );

  const visibleLineColumns = useMemo(
    () => LINE_ITEM_COLUMNS.filter(Boolean),
    [],
  );

  const historyLineItem = useMemo(
    () =>
      order.lineItems.find(
        (item) => item.lineItemId === historyOpenForLineItem,
      ),
    [historyOpenForLineItem, order.lineItems],
  );

  const handleCancelBulkAction = useCallback(() => {
    setBulkActionMode(null);
  }, []);

  const handleCloseConfirmDialog = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const handleCloseHistory = useCallback(() => {
    setHistoryOpenForLineItem(null);
  }, []);

  const handleSubmitBulkUpdate = useCallback(() => {
    if (confirmationItems.length === 0) {
      triggerToaster("Please select at least one eligible line item.", error);
      return;
    }

    setConfirmationMessage(
      getBulkConfirmationMessage({
        items: confirmationItems,
        isExpectedFgSelected,
        dueDate,
        formatDialogDate,
      }),
    );
    setOpenConfirmDialog(true);
  }, [confirmationItems, dueDate, isExpectedFgSelected, triggerToaster]);

  const handleConfirmBulkUpdate = useCallback(async () => {
    try {
      if (confirmationItems.length === 0) {
        triggerToaster("Please select at least one eligible line item.", error);
        return;
      }

      const updates = confirmationItems.map((item) => {
        if (isFgReadySelected) {
          return {
            lineItemId: item.lineItemId,
            fgReady: {
              quantity: parseMtValue(item.notInvoicedQty),
            },
            expectedFgData: [],
          };
        }

        // Expected FG selected
        return {
          lineItemId: item.lineItemId,
          fgReady: {
            quantity: 0,
          },
          expectedFgData: [
            {
              id: null,
              quantity: parseMtValue(item.notInvoicedQty),
              readinessDate: dueDate ? new Date(dueDate).getTime() : null,
              sellerOrderRef: item.sellerOrderRef || null,
              sellerOlRef: item.sellerOLRef || null,
            },
          ],
        };
      });

      const payload = {
        orderId: order.orderId,
        actionType: isFgReadySelected
          ? "MARK_ALL_FG_READY"
          : "MARK_ALL_EXPECTED_FG",
        updates,
      };

      const isFgUpdated = await updateFgReady(payload);
      if (!isFgUpdated) {
        return;
      }
      setOpenConfirmDialog(false);
      setBulkActionMode(null);
      setDueDate("");
      onToggleSelectAll(order.orderId, true);
    } catch (err) {
      console.error(err);
      triggerToaster("Failed to submit bulk FG update", error);
    }
  }, [
    confirmationItems,
    dueDate,
    isFgReadySelected,
    onToggleSelectAll,
    order.orderId,
    triggerToaster,
    updateFgReady,
  ]);

  return (
    <Box
      sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1.5 }}
    >
      <FGBulkActions
        order={order}
        allEligibleSelected={allEligibleSelected}
        isEligiblePartiallySelected={isEligiblePartiallySelected}
        onToggleSelectAll={onToggleSelectAll}
        totalLineItemsQty={totalLineItemsQty}
        bulkActionMode={bulkActionMode}
        setBulkActionMode={setBulkActionMode}
        dueDate={dueDate}
        setDueDate={setDueDate}
        handleSubmitBulkUpdate={handleSubmitBulkUpdate}
        onCancelBulkAction={handleCancelBulkAction}
        isDueDateMissing={isDueDateMissing}
        shouldShowActionMessage={shouldShowActionMessage}
        showExpectedFgWarning={showExpectedFgWarning}
        effectiveSelectedEligibleCount={effectiveSelectedEligibleCount}
        formatDialogDate={formatDialogDate}
        selectedEligibleReducedCount={selectedEligibleReducedCount}
        totalEligibleQty={totalEligibleQty}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {visibleLineColumns.map((column) => (
                <TableCell key={column}>
                  <Typography variant="caption" color="text.secondary">
                    {column}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {order.lineItems.map((lineItem) => {
              const fgReadyTone = getLineItemFgReadyTone(lineItem);
              const expectedFgTone = getExpectedFgTone(lineItem);
              const fgAwaitedTone = getFgAwaitedTone(lineItem);
              const fgDueByTone = getFgDueByTone(lineItem);
              const isUpdateFgHidden = getIsUpdateFgHidden(lineItem);

              return (
                <Fragment key={lineItem.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {lineItem.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lineItem.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title={getQuantityBreakdownTooltip(lineItem)}
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: theme.palette.grey[800],
                              color: theme.palette.common.white,
                              borderRadius: 2,
                              px: 1.25,
                              py: 1,
                              fontSize: 12,
                              boxShadow: 3,
                            },
                          },
                          arrow: {
                            sx: {
                              color: theme.palette.grey[800],
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            textDecoration: "underline dotted",
                            textUnderlineOffset: "4px",
                            cursor: "help",
                          }}
                        >
                          {lineItem.orderQty}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: getFgToneColor(fgReadyTone, theme) }}
                      >
                        {lineItem.fgReady}
                      </Typography>
                      <BatchUpdatesList
                        batchUpdates={lineItem?.batchUpdates}
                        theme={theme}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: getFgToneColor(expectedFgTone, theme) }}
                      >
                        {lineItem.expectedFg}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: getFgToneColor(fgAwaitedTone, theme) }}
                      >
                        {lineItem.fgAwaited}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: getFgToneColor(fgDueByTone, theme) }}
                      >
                        {lineItem.fgDueBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          color="primary"
                          variant="body2"
                          onClick={() =>
                            setHistoryOpenForLineItem(lineItem.lineItemId)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {FG_TRACKING_LABELS.historyCta}
                        </Typography>
                        {!isUpdateFgHidden && (
                          <ButtonV1
                            title={FG_TRACKING_LABELS.updateFgCta}
                            variant="outlined"
                            color="secondary"
                            size="small"
                            style={{
                              minWidth: 72,
                              height: 26,
                              marginLeft: 2,
                            }}
                            textStyle={{ fontSize: 12 }}
                            onClick={() => handleToggleFgUpdate(lineItem)}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {openFgUpdateIds.has(lineItem.lineItemId) && (
                    <TableRow>
                      <TableCell colSpan={visibleLineColumns.length}>
                        <FGUpdate
                          fgUpdateData={fgUpdateData[lineItem.lineItemId]}
                          lineItem={lineItem}
                          onSubmit={(data) =>
                            handleFGUpdateData(data, lineItem)
                          }
                          handleToggleFgUpdate={handleToggleFgUpdate}
                          orderType={order.orderType}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ModalBox
        open={Boolean(historyLineItem)}
        width="40%"
        onCloseModal={handleCloseHistory}
      >
        {!!historyLineItem && (
          <FGHistory
            materialId={historyLineItem.id}
            materialDescription={historyLineItem.description}
            orderedQty={historyLineItem.orderQty}
            orderId={order.orderId}
            lineItemId={historyLineItem.lineItemId}
          />
        )}
      </ModalBox>
      <FGConfirmDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmBulkUpdate}
        isExpectedFgSelected={isExpectedFgSelected}
        confirmationMessage={confirmationMessage}
        confirmationItems={confirmationItems}
        dueDate={dueDate}
        formatDialogDate={formatDialogDate}
      />
    </Box>
  );
};

FGLineItemTable.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        orderQty: PropTypes.string.isRequired,
        fgReady: PropTypes.string.isRequired,
        expectedFg: PropTypes.string.isRequired,
        fgAwaited: PropTypes.string.isRequired,
        fgDueBy: PropTypes.string.isRequired,
        eligible: PropTypes.bool.isRequired,
        invoicedQty: PropTypes.string,
        notInvoicedQty: PropTypes.string,
        shortCloseQty: PropTypes.string,
        orderConfirmedAt: PropTypes.string,
        hasPayment: PropTypes.bool,
        doCreatedQtyMt: PropTypes.number,
      }),
    ).isRequired,
  }).isRequired,
  selectedLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
};

export default memo(FGLineItemTable);
